import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Textarea } from '../../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import {
  MessageSquare,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Upload,
  Loader2,
  User,
  Calendar,
  Target,
  Send,
  Eye,
  ChevronDown,
  Info,
  Zap,
  FileUp,
  HelpCircle,
  LayoutList,
  ClipboardCheck,
  CheckSquare,
  Square,
  Plus,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { serviceRequestAPI, taskAPI } from '../../../services/api';
import TaskCard from '../../shared/TaskCard';
import ServiceChat from '../ServiceChat';
import FileUpload from '../../shared/FileUpload';
import ProcessStepper from './ProcessStepper';
import Deliverables from './Deliverables';
import FAQAccordion from './FAQAccordion';
import SocialProof from './SocialProof';
import FormDataViewer from './FormDataViewer';
import ServiceFormModal from './ServiceFormModal';
import { getSlugFromType } from '../../../data/serviceData';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  initSocket,
  onServiceRequestUpdated,
  offServiceRequestUpdated,
  onTaskCreated,
  onTaskUpdated,
  offTaskCreated,
  offTaskUpdated,
} from '../../../services/socket';

const STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: { label: 'Pending Assignment', color: 'bg-slate-100 text-slate-700', progress: 10, stepIndex: 0 },
  ASSIGNED: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', progress: 30, stepIndex: 2 },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-amber-100 text-amber-700', progress: 60, stepIndex: 3 },
  ON_HOLD: { label: 'On Hold', color: 'bg-orange-100 text-orange-700', progress: 50, stepIndex: 3 },
  COMPLETED: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', progress: 100, stepIndex: 4 },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-700', progress: 0, stepIndex: 0 },
};

// ── Shared visual tokens ─────────────────────────────────────────
const glassCard = {
  background: 'linear-gradient(135deg, rgba(255,255,255,0.88) 0%, rgba(248,247,255,0.92) 100%)',
  border: '1px solid rgba(255,255,255,0.7)',
  boxShadow: '0 2px 16px rgba(99,102,241,0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
};

const inputCls = (hasError) =>
  `bg-white/70 border transition-all duration-200 ${hasError ? 'border-red-400' : 'border-white/60 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100'}`;

// ── Skeleton loader ──────────────────────────────────────────────
function Skeleton({ className = '' }) {
  return <div className={`animate-pulse rounded-lg ${className}`} style={{ background: 'rgba(224,221,255,0.5)' }} />;
}

function WorkspaceSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-36 rounded-2xl" />
      <Skeleton className="h-20 rounded-2xl" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-4">
          <Skeleton className="h-10 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
        <div className="lg:col-span-2 space-y-4">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-28 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

// ── Action Banner ────────────────────────────────────────────────
function ActionBanner({ serviceRequest, pendingTasks, counselor, onTabSwitch, theme }) {
  const status = serviceRequest.status;

  if (status === 'COMPLETED') {
    return (
      <div
        className="flex items-start gap-3 p-4 rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(34,197,94,0.09), rgba(255,255,255,0.6))',
          border: '1px solid rgba(34,197,94,0.22)',
          boxShadow: '0 2px 12px rgba(34,197,94,0.08)',
        }}
      >
        <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-emerald-800">Your service is complete!</p>
          <p className="text-xs text-emerald-600 mt-0.5">Check the Documents tab for your deliverables, or send a message if you have questions.</p>
        </div>
      </div>
    );
  }

  if (status === 'PENDING_ADMIN_ASSIGNMENT') {
    return (
      <div
        className="flex items-start gap-3 p-4 rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(59,130,246,0.09), rgba(255,255,255,0.6))',
          border: '1px solid rgba(59,130,246,0.22)',
          boxShadow: '0 2px 12px rgba(59,130,246,0.08)',
        }}
      >
        <Clock className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-800">Your request is being reviewed</p>
          <p className="text-xs text-blue-600 mt-0.5">We'll assign a counselor within 1-2 business days. You'll be notified once assigned.</p>
        </div>
      </div>
    );
  }

  if (pendingTasks.length > 0) {
    return (
      <div
        className="flex items-start gap-3 p-4 rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(245,158,11,0.09), rgba(255,255,255,0.6))',
          border: '1px solid rgba(245,158,11,0.22)',
          boxShadow: '0 2px 12px rgba(245,158,11,0.08)',
        }}
      >
        <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800">
            You have {pendingTasks.length} pending task{pendingTasks.length > 1 ? 's' : ''}
          </p>
          <p className="text-xs text-amber-600 mt-0.5 truncate">
            Next: {pendingTasks[0].title}
          </p>
        </div>
        <Button
          size="sm"
          className="rounded-xl text-white text-xs shrink-0 border-0"
          style={{
            background: 'linear-gradient(135deg, #F59E0B, #D97706)',
            boxShadow: '0 4px 12px rgba(245,158,11,0.3)',
          }}
          onClick={() => onTabSwitch('tasks')}
        >
          View Tasks
        </Button>
      </div>
    );
  }

  if (status === 'ASSIGNED' && !pendingTasks.length) {
    return (
      <div
        className="flex items-start gap-3 p-4 rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(59,130,246,0.09), rgba(255,255,255,0.6))',
          border: '1px solid rgba(59,130,246,0.22)',
          boxShadow: '0 2px 12px rgba(59,130,246,0.08)',
        }}
      >
        <User className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-blue-800">
            {counselor ? `${counselor.firstName} ${counselor.lastName} is assigned to your service` : 'A counselor has been assigned'}
          </p>
          <p className="text-xs text-blue-600 mt-0.5">They'll add tasks and reach out soon. Feel free to send a message.</p>
        </div>
        {counselor && (
          <Button
            size="sm"
            variant="outline"
            className="rounded-xl border-blue-200/70 text-blue-700 text-xs shrink-0 bg-white/60"
            onClick={() => onTabSwitch('messages')}
          >
            Send Message
          </Button>
        )}
      </div>
    );
  }

  // Default: In Progress, no pending tasks
  return (
    <div
      className="flex items-start gap-3 p-4 rounded-2xl"
      style={{
        background: `linear-gradient(135deg, ${theme.accent}0d, rgba(255,255,255,0.6))`,
        border: `1px solid ${theme.accent}25`,
        boxShadow: `0 2px 12px ${theme.accent}10`,
      }}
    >
      <RefreshCw className="w-5 h-5 mt-0.5 shrink-0" style={{ color: theme.accent }} />
      <div>
        <p className="text-sm font-semibold text-gray-800">Your counselor is working on your service</p>
        <p className="text-xs text-gray-500 mt-0.5">Nothing needed from you right now. Check back soon or send a message.</p>
      </div>
    </div>
  );
}

// ── Documents Tab Content ─────────────────────────────────────────
function DocumentsTab({ serviceData, theme }) {
  const [checkedDocs, setCheckedDocs] = useState(() => serviceData.prerequisites.map(() => false));

  const toggleDoc = (idx) => {
    setCheckedDocs(prev => { const n = [...prev]; n[idx] = !n[idx]; return n; });
  };

  const checkedCount = checkedDocs.filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Upload area */}
      <div
        className="rounded-2xl p-8 text-center cursor-pointer transition-all duration-200"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.65), rgba(248,247,255,0.5))',
          border: `2px dashed ${theme.accent}28`,
          boxShadow: '0 2px 12px rgba(99,102,241,0.04)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${theme.accent}50`; e.currentTarget.style.background = `linear-gradient(135deg, ${theme.accent}06, rgba(248,247,255,0.6))`; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${theme.accent}28`; e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.65), rgba(248,247,255,0.5))'; }}
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
          style={{ background: `${theme.accent}10` }}
        >
          <FileUp className="w-6 h-6" style={{ color: theme.accent }} />
        </div>
        <p className="text-sm font-medium text-gray-600">Drag & drop files here, or click to browse</p>
        <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG up to 10MB</p>
      </div>

      {/* Document checklist */}
      <div className="rounded-2xl overflow-hidden" style={glassCard}>
        <div
          className="p-4 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.6)', background: `linear-gradient(135deg, ${theme.accent}08, rgba(255,255,255,0.3))` }}
        >
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-800">Required Documents</h4>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${theme.accent}12`, color: theme.accent }}>
              {checkedCount}/{serviceData.prerequisites.length} ready
            </span>
          </div>
          <Progress value={(checkedCount / serviceData.prerequisites.length) * 100} className="h-1.5 mt-2.5 rounded-full" />
        </div>
        <div className="divide-y" style={{ '--tw-divide-opacity': 1 }}>
          {serviceData.prerequisites.map((doc, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 p-3.5 transition-colors"
              style={{ borderColor: 'rgba(255,255,255,0.5)' }}
            >
              <button onClick={() => toggleDoc(idx)} className="shrink-0">
                {checkedDocs[idx]
                  ? <CheckSquare className="w-5 h-5 text-emerald-500" />
                  : <Square className="w-5 h-5 text-gray-300" />
                }
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${checkedDocs[idx] ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{doc}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {checkedDocs[idx] ? 'Marked as ready' : 'Not uploaded yet'}
                </p>
              </div>
              {!checkedDocs[idx] && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl text-xs shrink-0 bg-white/60 border-white/60"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  Upload
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {serviceData.prerequisites.length === 0 && (
        <div className="p-8 rounded-2xl text-center" style={glassCard}>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(99,102,241,0.06)' }}>
            <FileText className="w-6 h-6 text-gray-300" />
          </div>
          <p className="text-gray-500 font-medium">No documents uploaded yet</p>
          <p className="text-sm text-gray-400 mt-1">Your counselor will let you know which documents are needed.</p>
        </div>
      )}
    </div>
  );
}

// ── Contextual FAQ Sidebar ───────────────────────────────────────
function ContextualFAQ({ faqs, status, onViewAll }) {
  const [openIdx, setOpenIdx] = useState(null);

  const contextFaqs = useMemo(() => {
    if (!faqs?.length) return [];
    return faqs.slice(0, 3);
  }, [faqs]);

  return (
    <div className="space-y-2">
      {contextFaqs.map((faq, idx) => (
        <div
          key={idx}
          className="rounded-xl overflow-hidden transition-all duration-200"
          style={{
            background: openIdx === idx
              ? 'linear-gradient(135deg, rgba(255,255,255,0.75), rgba(248,247,255,0.65))'
              : 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(255,255,255,0.6)',
            boxShadow: openIdx === idx ? '0 2px 8px rgba(99,102,241,0.08)' : 'none',
          }}
        >
          <button
            onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
            className="w-full flex items-center justify-between p-3 text-left"
          >
            <span className="text-xs font-medium text-gray-700 pr-2 leading-snug">{faq.q}</span>
            <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${openIdx === idx ? 'rotate-180' : ''}`} />
          </button>
          <div
            className="overflow-hidden transition-all duration-200"
            style={{ maxHeight: openIdx === idx ? '120px' : '0px', opacity: openIdx === idx ? 1 : 0 }}
          >
            <p className="px-3 pb-3 text-xs text-gray-500 leading-relaxed">{faq.a}</p>
          </div>
        </div>
      ))}
      {faqs?.length > 3 && (
        <button onClick={onViewAll} className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors w-full text-center pt-1">
          View all FAQs →
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function TrackingDashboard({ serviceRequestId, serviceData, theme }) {
  const [serviceRequest, setServiceRequest] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Task submission modal
  const [submitTaskOpen, setSubmitTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [submissionText, setSubmissionText] = useState('');
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Apply for more services modal
  const [showApplyMoreModal, setShowApplyMoreModal] = useState(false);
  const serviceSlug = useMemo(() => serviceRequest ? getSlugFromType(serviceRequest.serviceType) : null, [serviceRequest]);

  const handleApplyMoreSubmit = async (formData, representativeName) => {
    const metadata = {};
    await serviceRequestAPI.create(
      serviceRequest.serviceType,
      metadata,
      formData || null,
      representativeName,
    );
    toast.success('New service application submitted successfully!');
    setShowApplyMoreModal(false);
    fetchData();
  };

  const fetchData = useCallback(async () => {
    try {
      const [requestRes, tasksRes] = await Promise.all([
        serviceRequestAPI.getById(serviceRequestId),
        taskAPI.getAll({ serviceRequestId }),
      ]);
      setServiceRequest(requestRes.serviceRequest);
      setTasks(tasksRes.tasks?.filter(t => t.serviceRequestId === serviceRequestId) || []);
    } catch {
      toast.error('Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, [serviceRequestId]);

  useEffect(() => {
    fetchData();
    initSocket();

    onServiceRequestUpdated((updated) => {
      if (updated.serviceRequestId === serviceRequestId) setServiceRequest(updated);
    });
    onTaskCreated((newTask) => {
      if (newTask.serviceRequestId === serviceRequestId) {
        setTasks(prev => [newTask, ...prev]);
        toast.info(`New task: ${newTask.title}`);
      }
    });
    onTaskUpdated((updated) => {
      setTasks(prev => prev.map(t => t.taskId === updated.taskId ? updated : t));
    });

    return () => { offServiceRequestUpdated(); offTaskCreated(); offTaskUpdated(); };
  }, [fetchData, serviceRequestId]);

  useEffect(() => {
    if (!serviceRequest || loading) return;
    const pending = tasks.filter(t => ['PENDING', 'IN_PROGRESS', 'REVISION_REQUIRED'].includes(t.status));
    if (pending.length > 0) setActiveTab('tasks');
  }, [serviceRequest, loading, tasks]);

  const handleSubmitTask = (task) => {
    setSelectedTask(task);
    setSubmissionText('');
    setSubmissionFiles([]);
    setSubmitTaskOpen(true);
  };

  const handleTaskSubmission = async () => {
    if (!submissionText.trim() && submissionFiles.length === 0) {
      toast.error('Please provide text or upload files');
      return;
    }
    setSubmitting(true);
    try {
      await taskAPI.submit(selectedTask.taskId, submissionText, submissionFiles);
      toast.success('Task submitted successfully!');
      setSubmitTaskOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUploadComplete = (uploadedFiles) => {
    setSubmissionFiles(prev => [...prev, ...uploadedFiles]);
  };

  // ── Loading ───────────────────────────────────────────────────
  if (loading) return <WorkspaceSkeleton />;

  if (!serviceRequest) {
    return (
      <div className="p-8 rounded-2xl text-center" style={glassCard}>
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(99,102,241,0.07)' }}>
          <AlertCircle className="w-7 h-7 text-gray-300" />
        </div>
        <p className="text-gray-600 font-semibold">Could not load workspace</p>
        <p className="text-sm text-gray-400 mt-1">There was an issue loading your service details.</p>
        <Button
          onClick={fetchData}
          variant="outline"
          className="mt-4 rounded-xl text-sm bg-white/60 border-white/70"
        >
          Try Again
        </Button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[serviceRequest.status] || {};
  const counselor = serviceRequest.counselor || serviceRequest.assignedCounselorInfo;
  const agent = serviceRequest.agent || serviceRequest.assignedAgentInfo;
  const pendingTasks = tasks.filter(t => ['PENDING', 'IN_PROGRESS', 'REVISION_REQUIRED'].includes(t.status));
  const completedTasks = tasks.filter(t => t.status === 'COMPLETED');
  const stepIndex = statusConfig.stepIndex ?? 0;
  const completedSteps = serviceData.processSteps.map((_, idx) => idx < stepIndex);

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* ━━━ Compact Status Header ━━━ */}
      <div
        className="rounded-3xl overflow-hidden"
        style={{
          background: `linear-gradient(145deg, ${theme.accent}0d 0%, rgba(255,255,255,0.92) 60%, rgba(248,247,255,0.95) 100%)`,
          border: `1px solid ${theme.accent}20`,
          boxShadow: `0 4px 24px ${theme.accent}12, inset 0 1px 0 rgba(255,255,255,0.9)`,
        }}
      >
        <div className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3.5">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${theme.accent}18, ${theme.accent}0c)`,
                  border: `1px solid ${theme.accent}20`,
                }}
              >
                {React.createElement(serviceData.icon, { className: 'w-5 h-5', style: { color: theme.accent } })}
              </div>
              <div>
                <div className="flex items-center gap-2.5">
                  <h2 className="text-lg font-semibold text-gray-900">{serviceData.name}</h2>
                  <Badge className={`${statusConfig.color} rounded-full text-xs px-2.5 py-0.5 border-0`}>
                    {statusConfig.label}
                  </Badge>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  Request #{serviceRequest.serviceRequestId?.slice(0, 8)} · Applied {format(new Date(serviceRequest.appliedAt || serviceRequest.createdAt), 'MMM d, yyyy')}
                  {counselor ? ` · Counselor: ${counselor.firstName} ${counselor.lastName}` : ' · Counselor: TBD'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <span className="text-2xl font-bold" style={{ color: theme.accent }}>{statusConfig.progress || 0}%</span>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Complete</p>
              </div>
            </div>
          </div>
          <div className="mt-4 h-2 rounded-full overflow-hidden" style={{ background: `${theme.accent}14` }}>
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${statusConfig.progress || 0}%`,
                background: `linear-gradient(90deg, ${theme.accent}99, ${theme.accent})`,
              }}
            />
          </div>
        </div>

        {/* Action Banner */}
        <div className="px-5 pb-4 -mt-1">
          <ActionBanner
            serviceRequest={serviceRequest}
            pendingTasks={pendingTasks}
            counselor={counselor}
            onTabSwitch={setActiveTab}
            theme={theme}
          />
        </div>
      </div>

      {/* ━━━ Live Progress Stepper ━━━ */}
      <div className="p-5 rounded-2xl" style={glassCard}>
        <ProcessStepper
          steps={serviceData.processSteps}
          activeStep={stepIndex}
          completedSteps={completedSteps}
          theme={theme}
        />
      </div>

      {/* ━━━ Two Column Workspace ━━━ */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* LEFT — Main Workspace (3/5 = 60%) */}
        <div className="lg:col-span-3">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList
              className="w-full justify-start rounded-2xl p-1 h-auto gap-0.5 overflow-x-auto"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.85), rgba(248,247,255,0.9))',
                border: '1px solid rgba(255,255,255,0.7)',
                boxShadow: '0 2px 12px rgba(99,102,241,0.06)',
              }}
            >
              {[
                { value: 'overview', label: 'Overview', icon: LayoutList },
                { value: 'tasks', label: 'Tasks', icon: ClipboardCheck, count: pendingTasks.length },
                { value: 'messages', label: 'Messages', icon: MessageSquare },
                { value: 'documents', label: 'Documents', icon: FileText },
                { value: 'info', label: 'Service Info', icon: Info },
              ].map(tab => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="relative rounded-xl px-3.5 py-2 text-xs font-medium text-gray-500 transition-all duration-200 data-[state=active]:shadow-sm data-[state=active]:text-white"
                  style={activeTab === tab.value
                    ? {
                        background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}cc)`,
                        color: 'white',
                        boxShadow: `0 2px 10px ${theme.accent}44`,
                      }
                    : undefined
                  }
                >
                  <tab.icon className="w-3.5 h-3.5 mr-1.5" />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[17px] h-[17px] flex items-center justify-center leading-none px-1">
                      {tab.count}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* ── Overview Tab ── */}
            <TabsContent value="overview" className="space-y-4 mt-5">
              {/* What's happening now */}
              <div className="p-5 rounded-2xl" style={glassCard}>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">What's happening now</h3>
                {serviceRequest.status === 'PENDING_ADMIN_ASSIGNMENT' && (
                  <p className="text-sm text-gray-600 leading-relaxed">Your application is under review. Our team will assign a counselor to your service within 1-2 business days.</p>
                )}
                {serviceRequest.status === 'ASSIGNED' && counselor && (
                  <p className="text-sm text-gray-600 leading-relaxed">Your counselor <span className="font-semibold text-gray-800">{counselor.firstName} {counselor.lastName}</span> is getting familiar with your profile. They'll reach out with your first tasks soon.</p>
                )}
                {serviceRequest.status === 'IN_PROGRESS' && counselor && (
                  <p className="text-sm text-gray-600 leading-relaxed">Your counselor <span className="font-semibold text-gray-800">{counselor.firstName} {counselor.lastName}</span> is actively working on your service. {pendingTasks.length > 0 ? `You have ${pendingTasks.length} pending task${pendingTasks.length > 1 ? 's' : ''} to complete.` : 'Nothing needed from you right now.'}</p>
                )}
                {serviceRequest.status === 'COMPLETED' && (
                  <p className="text-sm text-gray-600 leading-relaxed">Your service has been completed! Check the Documents tab for any deliverables. Thank you for choosing Fly8.</p>
                )}
                {serviceRequest.status === 'ON_HOLD' && (
                  <p className="text-sm text-gray-600 leading-relaxed">Your service is currently on hold. This usually means we're waiting for additional information. Check your tasks or send a message to your counselor.</p>
                )}
                {!['PENDING_ADMIN_ASSIGNMENT', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'ON_HOLD'].includes(serviceRequest.status) && (
                  <p className="text-sm text-gray-600 leading-relaxed">Your service request is being processed.</p>
                )}
              </div>

              {/* Pre-Application Form Summary */}
              {serviceRequest.formData && (
                <div className="p-5 rounded-2xl" style={glassCard}>
                  <FormDataViewer
                    serviceType={serviceRequest.serviceType}
                    formData={serviceRequest.formData}
                  />
                </div>
              )}

              {/* Apply for more services */}
              {serviceSlug && serviceSlug !== 'profile-assessment' && (
                <div
                  className="group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300"
                  style={{
                    background: `linear-gradient(135deg, ${theme.accent}0a 0%, rgba(255,255,255,0.8) 100%)`,
                    border: `1px solid ${theme.accent}20`,
                    boxShadow: `0 2px 16px ${theme.accent}0c`,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 6px 24px ${theme.accent}20`; e.currentTarget.style.borderColor = `${theme.accent}35`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 2px 16px ${theme.accent}0c`; e.currentTarget.style.borderColor = `${theme.accent}20`; }}
                  onClick={() => setShowApplyMoreModal(true)}
                >
                  <div className="relative p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                    <div
                      className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105"
                      style={{
                        background: `linear-gradient(135deg, ${theme.accent}18, ${theme.accent}0c)`,
                        color: theme.accent,
                      }}
                    >
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900">Expand your application</h3>
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                        Add more sub-services to your {serviceData.name} package and get comprehensive support.
                      </p>
                    </div>
                    <Button
                      className="w-full sm:w-auto rounded-xl text-xs font-semibold gap-2 px-5 h-9 text-white border-0 transition-all duration-300 shrink-0"
                      style={{
                        background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}cc)`,
                        boxShadow: `0 4px 14px ${theme.accent}40`,
                      }}
                      onClick={e => { e.stopPropagation(); setShowApplyMoreModal(true); }}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Apply for More
                      <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* What you need to do */}
              {pendingTasks.length > 0 ? (
                <div
                  className="p-5 rounded-2xl"
                  style={{
                    ...glassCard,
                    borderLeft: `3px solid ${theme.accent}`,
                  }}
                >
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4" style={{ color: theme.accent }} />
                    What you need to do
                  </h3>
                  <div className="space-y-2.5">
                    {pendingTasks.slice(0, 3).map(task => (
                      <TaskCard
                        key={task.taskId}
                        task={task}
                        variant="compact"
                        onSubmit={() => handleSubmitTask(task)}
                      />
                    ))}
                    {pendingTasks.length > 3 && (
                      <Button
                        variant="outline"
                        className="w-full rounded-xl text-xs bg-white/60 border-white/60"
                        onClick={() => setActiveTab('tasks')}
                      >
                        View all {pendingTasks.length} tasks
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-5 rounded-2xl" style={glassCard}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: 'rgba(34,197,94,0.1)' }}
                    >
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Nothing needed from you right now</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {tasks.length === 0
                          ? 'Your counselor will add tasks as your service progresses. This usually happens within 1-2 business days.'
                          : 'All tasks are up to date. Your counselor is working on the next steps.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Task stats */}
              {tasks.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: pendingTasks.length, label: 'Pending', color: '#F59E0B' },
                    { value: completedTasks.length, label: 'Done', color: '#22C55E' },
                    { value: tasks.length, label: 'Total', color: '#0EA5E9' },
                  ].map(({ value, label, color }) => (
                    <div
                      key={label}
                      className="p-3.5 rounded-2xl text-center"
                      style={{
                        background: `linear-gradient(135deg, ${color}0c, rgba(255,255,255,0.7))`,
                        border: `1px solid ${color}20`,
                        boxShadow: `0 2px 10px ${color}0e`,
                      }}
                    >
                      <p className="text-xl font-bold" style={{ color }}>{value}</p>
                      <p className="text-[11px] text-gray-500 font-medium mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Recent Activity */}
              {serviceRequest.statusHistory?.length > 0 && (
                <div className="p-5 rounded-2xl" style={glassCard}>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Recent Activity</h3>
                  <div className="space-y-2.5">
                    {serviceRequest.statusHistory.slice(0, 5).map((entry, idx) => {
                      const sc = STATUS_CONFIG[entry.status];
                      return (
                        <div key={idx} className="flex gap-3 items-start">
                          <div
                            className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                            style={{ background: theme.accent }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-600">
                              Status → <span className="font-semibold text-gray-800">{sc?.label || entry.status}</span>
                              {entry.note && <span className="text-gray-400"> — {entry.note}</span>}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              {format(new Date(entry.changedAt), 'MMM d, yyyy h:mm a')}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Notes */}
              {serviceRequest.notes?.filter(n => !n.isInternal).length > 0 && (
                <div className="p-5 rounded-2xl" style={glassCard}>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-400" />
                    Notes
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {serviceRequest.notes.filter(n => !n.isInternal).slice(0, 5).map((note, idx) => (
                      <div
                        key={idx}
                        className="p-3 rounded-xl"
                        style={{
                          background: 'rgba(255,255,255,0.6)',
                          border: '1px solid rgba(255,255,255,0.7)',
                        }}
                      >
                        <p className="text-sm text-gray-700">{note.text}</p>
                        <p className="text-[11px] text-gray-400 mt-1">{format(new Date(note.addedAt), 'MMM d h:mm a')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ── Tasks Tab ── */}
            <TabsContent value="tasks" className="mt-5">
              {tasks.length === 0 ? (
                <div className="p-10 text-center rounded-2xl" style={glassCard}>
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
                    style={{ background: 'rgba(99,102,241,0.07)' }}
                  >
                    <ClipboardCheck className="w-7 h-7 text-gray-300" />
                  </div>
                  <p className="text-gray-500 font-semibold">No tasks assigned yet</p>
                  <p className="text-sm text-gray-400 mt-1.5 max-w-sm mx-auto">Your counselor will create tasks as your service progresses. This usually happens within 1-2 business days after assignment.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {pendingTasks.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Your Pending Tasks</h4>
                      <div className="space-y-2.5">
                        {pendingTasks.map(task => (
                          <TaskCard key={task.taskId} task={task} onSubmit={() => handleSubmitTask(task)} onView={() => handleSubmitTask(task)} />
                        ))}
                      </div>
                    </div>
                  )}

                  {completedTasks.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Completed</h4>
                      <div className="space-y-2.5">
                        {completedTasks.map(task => (
                          <TaskCard key={task.taskId} task={task} onSubmit={() => handleSubmitTask(task)} onView={() => handleSubmitTask(task)} />
                        ))}
                      </div>
                    </div>
                  )}

                  {tasks.filter(t => !['PENDING', 'IN_PROGRESS', 'REVISION_REQUIRED', 'COMPLETED'].includes(t.status)).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">In Review</h4>
                      <div className="space-y-2.5">
                        {tasks.filter(t => !['PENDING', 'IN_PROGRESS', 'REVISION_REQUIRED', 'COMPLETED'].includes(t.status)).map(task => (
                          <TaskCard key={task.taskId} task={task} onSubmit={() => handleSubmitTask(task)} onView={() => handleSubmitTask(task)} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* ── Messages Tab ── */}
            <TabsContent value="messages" className="mt-5">
              {(counselor || agent) ? (
                <div className="space-y-3">
                  <div
                    className="p-4 rounded-2xl"
                    style={glassCard}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                        style={{
                          background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}cc)`,
                          boxShadow: `0 4px 12px ${theme.accent}40`,
                        }}
                      >
                        {(counselor || agent).firstName?.[0]}{(counselor || agent).lastName?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{(counselor || agent).firstName} {(counselor || agent).lastName}</p>
                        <p className="text-xs text-gray-400">{counselor ? 'Counselor' : 'Agent'} · Typically responds within 4 hours</p>
                      </div>
                    </div>
                  </div>
                  <ServiceChat
                    serviceRequestId={serviceRequest.serviceRequestId}
                    counselor={counselor}
                    agent={agent}
                  />
                </div>
              ) : (
                <div className="p-10 text-center rounded-2xl" style={glassCard}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(99,102,241,0.07)' }}>
                    <MessageSquare className="w-7 h-7 text-gray-300" />
                  </div>
                  <p className="text-gray-500 font-semibold">Chat will be available soon</p>
                  <p className="text-sm text-gray-400 mt-1.5 max-w-sm mx-auto">Once a counselor or agent is assigned to your service, you'll be able to message them directly here.</p>
                </div>
              )}
            </TabsContent>

            {/* ── Documents Tab ── */}
            <TabsContent value="documents" className="mt-5">
              <DocumentsTab serviceData={serviceData} theme={theme} />
            </TabsContent>

            {/* ── Service Info Tab ── */}
            <TabsContent value="info" className="space-y-5 mt-5">
              <div className="p-5 rounded-2xl" style={glassCard}>
                <h3 className="text-sm font-semibold text-gray-800 mb-4">What's Included</h3>
                <Deliverables deliverables={serviceData.deliverables} theme={theme} />
              </div>

              <div className="p-5 rounded-2xl" style={glassCard}>
                <h3 className="text-sm font-semibold text-gray-800 mb-4">Frequently Asked Questions</h3>
                <FAQAccordion faqs={serviceData.faqs} theme={theme} />
              </div>

              <div className="p-5 rounded-2xl" style={glassCard}>
                <h3 className="text-sm font-semibold text-gray-800 mb-4">How It Works</h3>
                <ProcessStepper steps={serviceData.processSteps} activeStep={-1} theme={theme} />
              </div>

              {serviceData.testimonials?.length > 0 && (
                <div
                  className="p-5 rounded-2xl"
                  style={{
                    background: `linear-gradient(135deg, ${theme.accent}09, rgba(255,255,255,0.85))`,
                    border: `1px solid ${theme.accent}18`,
                    boxShadow: `0 2px 16px ${theme.accent}0a`,
                  }}
                >
                  <h3 className="text-sm font-semibold text-gray-800 mb-4">What Students Say</h3>
                  <SocialProof stats={serviceData.stats} testimonials={serviceData.testimonials} theme={theme} />
                </div>
              )}

              <div className="p-5 rounded-2xl" style={glassCard}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">{serviceData.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Starting at {serviceData.price} · {serviceData.duration}</p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT — Sidebar (2/5 = 40%) */}
        <div className="lg:col-span-2 space-y-4">

          {/* R1: Counselor / Support Team */}
          <div className="rounded-2xl overflow-hidden" style={glassCard}>
            <div
              className="h-1 rounded-t-2xl"
              style={{ background: `linear-gradient(90deg, ${theme.accent}99, ${theme.accent})` }}
            />
            <div className="p-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Your Support Team</h4>
              {(counselor || agent) ? (
                <>
                  {counselor && (
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shrink-0"
                        style={{
                          background: `linear-gradient(135deg, ${theme.accent}20, ${theme.accent}10)`,
                          color: theme.accent,
                          border: `2px solid ${theme.accent}25`,
                        }}
                      >
                        {counselor.firstName?.[0]}{counselor.lastName?.[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">{counselor.firstName} {counselor.lastName}</p>
                        <p className="text-xs text-gray-500">Counselor</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Typically responds in 4 hours</p>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 rounded-xl text-xs text-white border-0"
                      style={{
                        background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}cc)`,
                        boxShadow: `0 4px 12px ${theme.accent}40`,
                      }}
                      onClick={() => setActiveTab('messages')}
                    >
                      <Send className="w-3 h-3 mr-1.5" />
                      Send Message
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center mx-auto mb-2" style={{ background: 'rgba(99,102,241,0.07)' }}>
                    <Clock className="w-5 h-5 text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-500 font-medium">Assigning support</p>
                  <p className="text-xs text-gray-400 mt-0.5">You'll be notified once assigned</p>
                </div>
              )}
              {agent && (
                <div
                  className="flex items-center gap-2.5 p-2.5 rounded-xl mt-3"
                  style={{
                    background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(255,255,255,0.5))',
                    border: '1px solid rgba(34,197,94,0.18)',
                  }}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-emerald-700 shrink-0" style={{ background: 'rgba(34,197,94,0.15)' }}>
                    {agent.firstName?.[0]}{agent.lastName?.[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{agent.firstName} {agent.lastName}</p>
                    <p className="text-[11px] text-gray-500">Agent</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* R2: Document Checklist (Quick View) */}
          <div className="p-4 rounded-2xl" style={glassCard}>
            <div className="flex items-center justify-between mb-2.5">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Documents</h4>
              <button
                onClick={() => setActiveTab('documents')}
                className="text-[11px] font-semibold hover:opacity-80 transition-opacity"
                style={{ color: theme.accent }}
              >
                View all
              </button>
            </div>
            <div className="space-y-1.5">
              {serviceData.prerequisites.slice(0, 4).map((doc, idx) => (
                <div key={idx} className="flex items-center gap-2 py-1">
                  <div
                    className="w-4 h-4 rounded shrink-0"
                    style={{ border: `1.5px solid rgba(99,102,241,0.2)`, background: 'rgba(255,255,255,0.6)' }}
                  />
                  <span className="text-xs text-gray-600 truncate">{doc}</span>
                </div>
              ))}
              {serviceData.prerequisites.length > 4 && (
                <p className="text-[11px] text-gray-400">+{serviceData.prerequisites.length - 4} more</p>
              )}
            </div>
          </div>

          {/* R3: Service Summary */}
          <div className="p-4 rounded-2xl" style={glassCard}>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Service Summary</h4>
            <div className="space-y-2">
              {[
                ['Service', serviceData.name],
                ['Price', serviceData.price],
                ['Applied', format(new Date(serviceRequest.appliedAt || serviceRequest.createdAt), 'MMM d, yyyy')],
                ['Status', statusConfig.label],
                ['Progress', `${statusConfig.progress || 0}%`],
                ['Est. Duration', serviceData.duration],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between items-center py-0.5">
                  <span className="text-xs text-gray-500">{label}</span>
                  <span className="text-xs font-semibold text-gray-800">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* R4: Timeline */}
          <div className="p-4 rounded-2xl" style={glassCard}>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              Timeline
            </h4>
            <div className="space-y-2.5 pl-1">
              <div className="flex items-center gap-2.5">
                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}cc)`, boxShadow: `0 0 6px ${theme.accent}50` }} />
                <span className="text-xs text-gray-500 w-20">Applied</span>
                <span className="text-xs font-semibold text-gray-800">{format(new Date(serviceRequest.appliedAt || serviceRequest.createdAt), 'MMM d, yyyy')}</span>
              </div>
              {serviceRequest.assignedAt ? (
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}cc)`, boxShadow: `0 0 6px ${theme.accent}50` }} />
                  <span className="text-xs text-gray-500 w-20">Assigned</span>
                  <span className="text-xs font-semibold text-gray-800">{format(new Date(serviceRequest.assignedAt), 'MMM d, yyyy')}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full border border-gray-200 shrink-0" />
                  <span className="text-xs text-gray-400 w-20">Assigned</span>
                  <span className="text-xs text-gray-400 italic">Pending</span>
                </div>
              )}
              {serviceRequest.completedAt ? (
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" style={{ boxShadow: '0 0 6px rgba(34,197,94,0.5)' }} />
                  <span className="text-xs text-gray-500 w-20">Completed</span>
                  <span className="text-xs font-semibold text-gray-800">{format(new Date(serviceRequest.completedAt), 'MMM d, yyyy')}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full border border-gray-200 shrink-0" />
                  <span className="text-xs text-gray-400 w-20">Completed</span>
                  <span className="text-xs text-gray-400 italic">—</span>
                </div>
              )}
            </div>
          </div>

          {/* R5: Quick Actions */}
          <div className="p-4 rounded-2xl" style={glassCard}>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" />
              Quick Actions
            </h4>
            <div className="space-y-1.5">
              {[
                { icon: FileUp, label: 'Upload Document', onClick: () => setActiveTab('documents') },
                ...((counselor || agent) ? [{ icon: MessageSquare, label: counselor ? 'Message Counselor' : 'Message Agent', onClick: () => setActiveTab('messages') }] : []),
                { icon: Eye, label: 'View All Services', onClick: () => window.location.href = '/dashboard/services' },
                { icon: HelpCircle, label: 'Get Help', onClick: () => window.location.href = '/dashboard/help' },
              ].map(({ icon: Icon, label, onClick }) => (
                <button
                  key={label}
                  onClick={onClick}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-medium text-gray-600 transition-all duration-200"
                  style={{ background: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.6)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${theme.accent}08`; e.currentTarget.style.borderColor = `${theme.accent}20`; e.currentTarget.style.color = theme.accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = '#4B5563'; }}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* R6: Contextual FAQ */}
          {serviceData.faqs?.length > 0 && (
            <div className="p-4 rounded-2xl" style={glassCard}>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <HelpCircle className="w-3.5 h-3.5" />
                Need Info?
              </h4>
              <ContextualFAQ
                faqs={serviceData.faqs}
                status={serviceRequest.status}
                onViewAll={() => setActiveTab('info')}
              />
            </div>
          )}
        </div>
      </div>

      {/* ━━━ Apply for More Services Modal ━━━ */}
      {serviceSlug && serviceSlug !== 'profile-assessment' && (
        <ServiceFormModal
          open={showApplyMoreModal}
          onOpenChange={setShowApplyMoreModal}
          serviceSlug={serviceSlug}
          onSubmit={handleApplyMoreSubmit}
        />
      )}

      {/* ━━━ Task Submission Modal ━━━ */}
      <Dialog open={submitTaskOpen} onOpenChange={setSubmitTaskOpen}>
        <DialogContent
          className="max-w-2xl p-0 rounded-[28px] border-0 overflow-hidden flex flex-col max-h-[85vh]"
          style={{
            background: 'linear-gradient(145deg, #f0efff 0%, #f5f3ff 40%, #eef2ff 80%, #f0f4ff 100%)',
            boxShadow: '0 32px 80px rgba(99,102,241,0.18), 0 8px 40px rgba(0,0,0,0.12)',
          }}
        >
          {selectedTask && (
            <>
              {/* Header */}
              <div
                className="px-6 pt-5 pb-4 border-b shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #d6d3f5 0%, #ddd9f9 45%, #d8d4f6 100%)',
                  borderColor: 'rgba(99,102,241,0.2)',
                  boxShadow: '0 4px 18px rgba(99,102,241,0.1)',
                }}
              >
                <DialogTitle className="text-lg font-bold text-gray-800">{selectedTask.title}</DialogTitle>
                <DialogDescription className="text-sm text-gray-500 mt-1">
                  {selectedTask.description || 'Complete this task by providing the required information.'}
                </DialogDescription>
              </div>

              {/* Body */}
              <div className="space-y-5 px-6 py-5 overflow-y-auto flex-1 min-h-0">
                {selectedTask.instructions && (
                  <div
                    className="p-4 rounded-2xl"
                    style={{
                      background: `linear-gradient(135deg, ${theme.accent}09, rgba(255,255,255,0.5))`,
                      border: `1px solid ${theme.accent}22`,
                    }}
                  >
                    <p className="text-sm font-semibold text-gray-800 mb-1">Instructions</p>
                    <p className="text-sm text-gray-600">{selectedTask.instructions}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Your Response</label>
                  <Textarea
                    placeholder="Enter your response here..."
                    value={submissionText}
                    onChange={(e) => setSubmissionText(e.target.value)}
                    rows={4}
                    className="bg-white/70 border-white/60 rounded-xl focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Upload Files <span className="font-normal text-gray-400">(Optional)</span></label>
                  <FileUpload onUploadComplete={handleFileUploadComplete} maxFiles={5} folder="task-submissions" />
                </div>
                {submissionFiles.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Uploaded Files</p>
                    <div className="flex flex-wrap gap-2">
                      {submissionFiles.map((file, idx) => (
                        <Badge
                          key={idx}
                          variant="secondary"
                          className="rounded-xl px-3 py-1 text-xs"
                          style={{ background: `${theme.accent}12`, color: theme.accent, border: `1px solid ${theme.accent}25` }}
                        >
                          {file.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div
                className="px-6 py-4 flex items-center justify-end gap-3 border-t shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #d6d3f5 0%, #ddd9f9 100%)',
                  borderColor: 'rgba(99,102,241,0.2)',
                  boxShadow: '0 -4px 18px rgba(99,102,241,0.1)',
                }}
              >
                <Button
                  variant="outline"
                  onClick={() => setSubmitTaskOpen(false)}
                  className="rounded-xl bg-white/60 border-white/70 text-gray-600 hover:bg-white/80"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleTaskSubmission}
                  disabled={submitting}
                  className="rounded-xl text-white border-0 hover:opacity-90 transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}cc)`,
                    boxShadow: `0 6px 22px ${theme.accent}44`,
                  }}
                >
                  {submitting
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                    : <><Upload className="w-4 h-4 mr-2" />Submit Task</>
                  }
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
